import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import { ButtonV3 } from 'components/ComponentV2';
import { ButtonV3Props } from 'components/ComponentV2/ButtonV3';
import { IconName } from 'components/Icon';
import IconButton from 'components/IconButton';
import { UserActionEnum } from 'hooks/useCompany/useCompanyInternal';
import { useResponsive } from 'hooks/useResponsive';
import { useCompany } from 'lib/providers/CompanyProvider';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { useAuth } from 'lib/providers/AuthProvider';

import styles from './CompanyActions.module.scss';

export const CompanyActions = ({ className, buttonSize = 'large' }: { className?: string; buttonSize?: ButtonV3Props['size'] }) => {
  const [t] = useTranslation('company');
  const screens = useResponsive();
  const {
    company: { following, id: companyId, website, career_page_link, custom_links },
    updateUserAction,
    isFollowLoading,
    socialMediaLinks,
  } = useCompany();
  const { isInitialAuthLoading } = useAuth();
  const followingId = useMemo(() => parseResponse(following)?.id, [following]);
  const customLinks = parseArrayResponse(custom_links);
  const showSocialMediaLinks = !career_page_link && customLinks.length === 0 && socialMediaLinks.length > 0;

  return (
    <div className={styles.container}>
      {showSocialMediaLinks && screens.sm && (
        <div className={styles.socials}>
          {socialMediaLinks.map(({ icon, url }) => (
            <a href={url} key={icon} rel="noreferrer" target="_blank">
              <IconButton iconName={icon as IconName} className={styles.icon} size="large" />
            </a>
          ))}
        </div>
      )}
      <div className={cx(styles.actions, className)}>
        {followingId ? (
          <ButtonV3
            color="primary"
            size={buttonSize}
            isLoading={isFollowLoading || isInitialAuthLoading}
            startIcon={'icon_check'}
            iconSize={screens.sm ? 'medium' : 'mediumplus'}
            startIconClassName={styles['following-icon']}
            onClick={() =>
              updateUserAction({
                userAction: UserActionEnum.UNFOLLOW_COMPANY,
                userActionId: followingId,
              })
            }
          >
            {t('actions.following')}
          </ButtonV3>
        ) : (
          <ButtonV3
            color="primary"
            size={buttonSize}
            isLoading={isFollowLoading || isInitialAuthLoading}
            onClick={() =>
              updateUserAction({
                userAction: UserActionEnum.FOLLOW_COMPANY,
                userActionId: companyId,
              })
            }
          >
            {t('actions.follow')}
          </ButtonV3>
        )}
        {website && (
          <a href={website} target="_blank" rel="noreferrer">
            <ButtonV3 color="secondary" size={buttonSize} startIcon="icon_launch">
              {t('actions.website')}
            </ButtonV3>
          </a>
        )}
      </div>
    </div>
  );
};

CompanyActions.displayName = 'CompanyActions';
